'use strict';

/**
 * <payment-qr-widget customer-id="..." customer-name="..." customer-mobile="...">
 * Compact payment QR carousel for the customer detail header, with an expanded (scannable) view
 * and, for users allowed to edit, a "Manage" window to add / edit / reorder / delete QRs.
 *
 * Wrong-customer safety: state is cleared the moment the customer changes, pending requests are cancelled,
 * and any response for another customer (or an older request) is ignored.
 */
angular.module('scrapQcrmApp')
    .directive('paymentQrWidget', function () {
        return {
            restrict: 'E',
            scope: {
                customerId: '<',
                customerName: '<',
                customerMobile: '<'
            },
            templateUrl: 'views/partials/payment-qr-widget.html',
            controller: 'PaymentQrWidgetCtrl'
        };
    })

    .controller('PaymentQrWidgetCtrl', function ($scope, $q, $uibModal, $timeout, customerPaymentQr) {

        var requestToken = 0;
        var canceller = null;

        $scope.state = emptyState();

        function emptyState() {
            return {
                customerId: null,
                loading: false,
                error: null,
                hidden: false,
                qrs: [],
                canEdit: false,
                maxQrs: customerPaymentQr.MAX_QRS,
                index: 0,
                urls: {},
                failed: {}
            };
        }

        function revokeAll(urls) {
            angular.forEach(urls, function (url) {
                URL.revokeObjectURL(url);
            });
        }

        function reset() {
            if (canceller) {
                canceller.resolve(); // cancels the requests of the previous customer
            }
            canceller = null;
            revokeAll($scope.state.urls);
            $scope.state = emptyState();
        }

        function load() {
            var customerId = $scope.customerId;
            reset();
            if (!customerId) {
                return;
            }
            var token = ++requestToken;
            canceller = $q.defer();
            var myCanceller = canceller;
            $scope.state.customerId = customerId;
            $scope.state.loading = true;

            customerPaymentQr.list(customerId, myCanceller.promise).then(function (res) {
                // a newer customer / reload started meanwhile: drop this answer
                if (token !== requestToken || $scope.customerId !== customerId) {
                    return;
                }
                $scope.state.loading = false;
                if (!res || res.status !== 'SUCCESS' || !res.data || res.data.customerId !== customerId) {
                    var code = res && res.error && res.error.code;
                    if (code === 'EC_PAYMENT_QR_NOT_ALLOWED') {
                        $scope.state.hidden = true; // roles without access see nothing
                        return;
                    }
                    $scope.state.error = (res && res.error && res.error.message) || 'Could not load payment QRs.';
                    return;
                }
                $scope.state.canEdit = !!res.data.canEdit;
                $scope.state.maxQrs = res.data.maxQrs || customerPaymentQr.MAX_QRS;
                $scope.state.qrs = (res.data.qrs || []).filter(function (qr) {
                    return qr.customerId === customerId;
                });
                $scope.state.index = 0;
                loadImages(customerId, token, myCanceller);
            }, function (err) {
                if (token !== requestToken) {
                    return; // cancelled because another customer was opened
                }
                $scope.state.loading = false;
                $scope.state.error = 'Could not load payment QRs.';
            });
        }

        function loadImages(customerId, token, myCanceller) {
            $scope.state.qrs.forEach(function (qr) {
                customerPaymentQr.imageUrl(customerId, qr.id, myCanceller.promise).then(function (url) {
                    if (token !== requestToken || $scope.customerId !== customerId) {
                        URL.revokeObjectURL(url);
                        return;
                    }
                    $scope.state.urls[qr.id] = url;
                }, function () {
                    if (token === requestToken) {
                        $scope.state.failed[qr.id] = true;
                    }
                });
            });
        }

        $scope.retry = load;

        // ---------- carousel (no wrap-around: arrows stop at the first and last QR) ----------

        $scope.current = function () {
            return $scope.state.qrs[$scope.state.index];
        };
        $scope.prev = function () {
            if ($scope.state.index > 0) {
                $scope.state.index--;
            }
        };
        $scope.next = function () {
            if ($scope.state.index < $scope.state.qrs.length - 1) {
                $scope.state.index++;
            }
        };
        $scope.goTo = function (i) {
            $scope.state.index = i;
        };
        $scope.onKeydown = function (event) {
            if (event.key === 'ArrowLeft') {
                $scope.prev();
                event.preventDefault();
            } else if (event.key === 'ArrowRight') {
                $scope.next();
                event.preventDefault();
            } else if (event.key === 'Enter' || event.key === ' ') {
                if ($scope.current()) {
                    $scope.openExpanded(event);
                    event.preventDefault();
                }
            }
        };
        $scope.displayUpi = function (qr) {
            return qr ? (qr.label || qr.upiId || '') : '';
        };

        // ---------- expanded view ----------

        $scope.openExpanded = function (event) {
            if (!$scope.state.qrs.length) {
                return;
            }
            var trigger = event && event.currentTarget;
            var modal = $uibModal.open({
                templateUrl: 'views/payment-qr-expanded-modal.html',
                controller: 'PaymentQrExpandedCtrl',
                size: 'lg',
                windowClass: 'payment-qr-expanded',
                ariaLabelledBy: 'payment-qr-expanded-title',
                resolve: {
                    data: function () {
                        return {
                            customerId: $scope.state.customerId,
                            customerName: $scope.customerName,
                            customerMobile: $scope.customerMobile,
                            qrs: $scope.state.qrs,
                            urls: $scope.state.urls,
                            failed: $scope.state.failed,
                            index: $scope.state.index
                        };
                    }
                }
            });
            modal.result.finally(function () {
                if (trigger && trigger.focus) {
                    $timeout(function () {
                        trigger.focus();
                    });
                }
            });
        };

        // ---------- manage (edit roles only; the API checks the role again) ----------

        $scope.openManage = function (event) {
            if (!$scope.state.canEdit) {
                return;
            }
            var trigger = event && event.currentTarget;
            var customerId = $scope.state.customerId;
            var modal = $uibModal.open({
                templateUrl: 'views/payment-qr-manage-modal.html',
                controller: 'PaymentQrManageCtrl',
                size: 'lg',
                backdrop: 'static',
                windowClass: 'payment-qr-manage',
                ariaLabelledBy: 'payment-qr-manage-title',
                resolve: {
                    data: function () {
                        return {
                            customerId: customerId,
                            customerName: $scope.customerName,
                            customerMobile: $scope.customerMobile
                        };
                    }
                }
            });
            modal.result.finally(function () {
                // reload only if the same customer is still open
                if ($scope.customerId === customerId) {
                    load();
                }
                if (trigger && trigger.focus) {
                    $timeout(function () {
                        trigger.focus();
                    });
                }
            });
        };

        $scope.$watch('customerId', function () {
            load();
        });

        $scope.$on('$destroy', function () {
            requestToken++;
            reset();
        });
    })

    .controller('PaymentQrExpandedCtrl', function ($scope, $document, $uibModalInstance, $timeout, growl, data) {

        $scope.data = data;
        $scope.index = Math.min(Math.max(data.index || 0, 0), data.qrs.length - 1);

        $scope.current = function () {
            return data.qrs[$scope.index];
        };
        $scope.prev = function () {
            if ($scope.index > 0) {
                $scope.index--;
            }
        };
        $scope.next = function () {
            if ($scope.index < data.qrs.length - 1) {
                $scope.index++;
            }
        };
        $scope.goTo = function (i) {
            $scope.index = i;
        };
        $scope.displayUpi = function (qr) {
            return qr ? (qr.label || qr.upiId || '') : '';
        };
        // arrow keys anywhere in the open dialog (focus sits on the modal window, outside this template)
        function onDocKeydown(event) {
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
                return;
            }
            var target = event.target;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) {
                return;
            }
            $scope.$applyAsync(function () {
                if (event.key === 'ArrowLeft') {
                    $scope.prev();
                } else {
                    $scope.next();
                }
            });
            event.preventDefault();
        }
        $document.on('keydown', onDocKeydown);
        $scope.$on('$destroy', function () {
            $document.off('keydown', onDocKeydown);
        });

        // small source images are scaled up without smoothing so the QR edges stay sharp
        $scope.crisp = {};
        $scope.onImageLoad = function (qrId, img) {
            $scope.crisp[qrId] = img.naturalWidth < 480;
        };

        $scope.copyUpi = function () {
            var text = $scope.displayUpi($scope.current());
            if (!text) {
                return;
            }
            var done = function () {
                $scope.copied = true;
                growl.success('UPI ID copied');
                $timeout(function () {
                    $scope.copied = false;
                }, 2000);
            };
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text).then(function () {
                    $scope.$applyAsync(done);
                }, function () {
                    $scope.$applyAsync(function () {
                        fallbackCopy(text) ? done() : growl.error('Could not copy');
                    });
                });
            } else {
                fallbackCopy(text) ? done() : growl.error('Could not copy');
            }
        };

        function fallbackCopy(text) {
            var area = document.createElement('textarea');
            area.value = text;
            area.setAttribute('readonly', '');
            area.style.position = 'fixed';
            area.style.opacity = '0';
            document.body.appendChild(area);
            area.select();
            var ok = false;
            try {
                ok = document.execCommand('copy');
            } catch (e) {
                ok = false;
            }
            document.body.removeChild(area);
            return ok;
        }

        $scope.downloadName = function () {
            var qr = $scope.current();
            var ext = qr && qr.contentType === 'image/webp' ? 'webp' : (qr && qr.contentType === 'image/jpeg' ? 'jpg' : 'png');
            var name = (data.customerName || 'customer').replace(/[^a-zA-Z0-9]+/g, '_').replace(/^_|_$/g, '');
            return 'Payment_QR_' + name + '_' + ($scope.index + 1) + '.' + ext;
        };

        // the image is in memory (blob URL), so the download link is made here rather than with ng-href
        $scope.download = function () {
            var url = data.urls[$scope.current().id];
            if (!url) {
                return;
            }
            var link = document.createElement('a');
            link.href = url;
            link.download = $scope.downloadName();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        };

        $scope.close = function () {
            $uibModalInstance.dismiss('close');
        };
    })

    // pqr-image-load="expr($img)": runs when an <img> has loaded
    .directive('pqrImageLoad', function () {
        return {
            restrict: 'A',
            link: function (scope, element, attrs) {
                element.on('load', function () {
                    scope.$applyAsync(function () {
                        scope.$eval(attrs.pqrImageLoad, { $img: element[0] });
                    });
                });
            }
        };
    })

    .controller('PaymentQrManageCtrl', function ($scope, $q, $timeout, $uibModalInstance, growl, customerPaymentQr, data) {

        var customerId = data.customerId;
        var urls = {};
        var loadedVersion = {}; // qr id -> updatedon of the image shown

        $scope.data = data;
        $scope.qrs = [];
        $scope.canEdit = false;
        $scope.maxQrs = customerPaymentQr.MAX_QRS;
        $scope.loading = true;
        $scope.busy = false;
        $scope.urls = urls;
        $scope.failed = {};
        $scope.add = null;
        $scope.replace = null;
        $scope.editing = null;
        $scope.confirmDelete = null;

        function message(res, fallback) {
            return (res && res.error && res.error.message) || (res && res.data && res.data.error && res.data.error.message) || fallback;
        }

        function applyList(dataVM) {
            if (!dataVM || dataVM.customerId !== customerId) {
                return;
            }
            $scope.canEdit = !!dataVM.canEdit;
            $scope.maxQrs = dataVM.maxQrs || customerPaymentQr.MAX_QRS;
            $scope.qrs = (dataVM.qrs || []).filter(function (qr) {
                return qr.customerId === customerId;
            });
            $scope.qrs.forEach(function (qr) {
                if (!urls[qr.id] || loadedVersion[qr.id] !== qr.updatedon) {
                    loadImage(qr);
                }
            });
        }

        function loadImage(qr) {
            loadedVersion[qr.id] = qr.updatedon;
            customerPaymentQr.imageUrl(customerId, qr.id).then(function (url) {
                if (urls[qr.id]) {
                    URL.revokeObjectURL(urls[qr.id]);
                }
                urls[qr.id] = url;
                delete $scope.failed[qr.id];
            }, function () {
                $scope.failed[qr.id] = true;
            });
        }

        function reload() {
            $scope.loading = true;
            return customerPaymentQr.list(customerId).then(function (res) {
                $scope.loading = false;
                if (res && res.status === 'SUCCESS') {
                    applyList(res.data);
                } else {
                    growl.error(message(res, 'Could not load payment QRs.'));
                }
            }, function () {
                $scope.loading = false;
                growl.error('Could not load payment QRs.');
            });
        }

        // runs one change; refreshes the list from the answer; keeps the buttons disabled meanwhile
        function run(promise, successText) {
            $scope.busy = true;
            return promise.then(function (res) {
                $scope.busy = false;
                if (res && res.status === 'SUCCESS') {
                    applyList(res.data);
                    growl.success(successText);
                    return true;
                }
                growl.error(message(res, 'Something went wrong, please try again.'));
                if (res && res.error && res.error.code === 'EC_PAYMENT_QR_LIMIT_REACHED') {
                    reload();
                }
                return false;
            }, function () {
                $scope.busy = false;
                growl.error('Something went wrong, please try again.');
                return false;
            });
        }

        $scope.atLimit = function () {
            return $scope.qrs.length >= $scope.maxQrs;
        };

        $scope.displayUpi = function (qr) {
            return qr ? (qr.label || qr.upiId || '') : '';
        };

        // ---------- checking a picked file (add or replace) ----------

        function checkPicked(file, target) {
            target.file = file;
            target.error = null;
            target.checking = true;
            target.preview = null;
            if (target.localUrl) {
                URL.revokeObjectURL(target.localUrl);
                target.localUrl = null;
            }
            if (!file) {
                target.checking = false;
                return $q.when(false);
            }
            return customerPaymentQr.checkFile(file).then(function (error) {
                if (error) {
                    target.checking = false;
                    target.error = error;
                    return false;
                }
                target.localUrl = URL.createObjectURL(file);
                return customerPaymentQr.preview(customerId, file).then(function (res) {
                    target.checking = false;
                    if (!res || res.status !== 'SUCCESS' || res.data.customerId !== customerId) {
                        target.error = message(res, 'Could not read this image.');
                        return false;
                    }
                    target.preview = res.data;
                    return true;
                }, function () {
                    target.checking = false;
                    target.error = 'Could not check this image, please try again.';
                    return false;
                });
            });
        }

        function mismatch(label, upiId) {
            return !!label && !!upiId && label.trim().toLowerCase() !== upiId.toLowerCase();
        }

        // ---------- add ----------

        $scope.startAdd = function () {
            if ($scope.atLimit() || $scope.busy) {
                return;
            }
            $scope.replace = null;
            $scope.editing = null;
            $scope.confirmDelete = null;
            $scope.add = { label: '', confirmDuplicate: false, confirmMismatch: false, progress: 0 };
        };

        $scope.pickAddFile = function (file) {
            if (!$scope.add || !file) {
                return;
            }
            checkPicked(file, $scope.add).then(function (ok) {
                if (ok && $scope.add && $scope.add.preview.upiId && !$scope.add.label) {
                    $scope.add.label = $scope.add.preview.upiId; // pre-fill from the QR
                }
            });
        };

        $scope.addLabelInvalid = function () {
            return !!$scope.add && !!$scope.add.label && !!$scope.add.label.trim() &&
                !customerPaymentQr.isValidUpiId($scope.add.label);
        };
        $scope.addLabelMismatch = function () {
            return !!$scope.add && !!$scope.add.preview && mismatch($scope.add.label, $scope.add.preview.upiId);
        };
        $scope.canSaveAdd = function () {
            var a = $scope.add;
            return !!a && !!a.preview && !a.checking && !$scope.busy && !$scope.atLimit() && !$scope.addLabelInvalid() &&
                (!$scope.addLabelMismatch() || a.confirmMismatch) &&
                (!(a.preview.duplicates && a.preview.duplicates.length) || a.confirmDuplicate);
        };

        $scope.saveAdd = function () {
            if (!$scope.canSaveAdd()) {
                return;
            }
            var a = $scope.add;
            a.progress = 0;
            var upload = customerPaymentQr.add(customerId, a.file, (a.label || '').trim(), a.confirmDuplicate, a.confirmMismatch);
            run(upload.then(function (res) {
                return res.data;
            }, function (err) {
                return $q.reject(err);
            }, function (evt) {
                if (evt && evt.total) {
                    a.progress = Math.round(100 * evt.loaded / evt.total);
                }
            }), 'Payment QR added').then(function (ok) {
                if (ok) {
                    $scope.cancelAdd();
                }
            });
        };

        $scope.cancelAdd = function () {
            if ($scope.add && $scope.add.localUrl) {
                URL.revokeObjectURL($scope.add.localUrl);
            }
            $scope.add = null;
        };

        // ---------- replace image ----------

        $scope.startReplace = function (qr) {
            if ($scope.busy) {
                return;
            }
            $scope.add = null;
            $scope.editing = null;
            $scope.confirmDelete = null;
            var next = { qr: qr, confirmDuplicate: false, confirmMismatch: false, progress: 0 };
            if ($scope.replace) {
                // close the open panel first so its check block is rebuilt for the new QR
                $scope.cancelReplace();
                $timeout(function () {
                    $scope.replace = next;
                });
            } else {
                $scope.replace = next;
            }
        };
        $scope.pickReplaceFile = function (file) {
            if ($scope.replace && file) {
                checkPicked(file, $scope.replace);
            }
        };
        $scope.replaceLabelMismatch = function () {
            var r = $scope.replace;
            return !!r && !!r.preview && mismatch(r.qr.label, r.preview.upiId);
        };
        $scope.canSaveReplace = function () {
            var r = $scope.replace;
            return !!r && !!r.preview && !r.checking && !$scope.busy &&
                (!$scope.replaceLabelMismatch() || r.confirmMismatch) &&
                (!(r.preview.duplicates && r.preview.duplicates.length) || r.confirmDuplicate);
        };
        $scope.saveReplace = function () {
            if (!$scope.canSaveReplace()) {
                return;
            }
            var r = $scope.replace;
            var upload = customerPaymentQr.replaceImage(customerId, r.qr.id, r.file, r.confirmDuplicate, r.confirmMismatch);
            run(upload.then(function (res) {
                return res.data;
            }, function (err) {
                return $q.reject(err);
            }, function (evt) {
                if (evt && evt.total) {
                    r.progress = Math.round(100 * evt.loaded / evt.total);
                }
            }), 'QR image replaced').then(function (ok) {
                if (ok) {
                    $scope.cancelReplace();
                }
            });
        };
        $scope.cancelReplace = function () {
            if ($scope.replace && $scope.replace.localUrl) {
                URL.revokeObjectURL($scope.replace.localUrl);
            }
            $scope.replace = null;
        };

        // ---------- label ----------

        $scope.startEdit = function (qr) {
            if ($scope.busy) {
                return;
            }
            $scope.add = null;
            $scope.replace = null;
            $scope.confirmDelete = null;
            $scope.editing = { qr: qr, label: qr.label || '', confirmMismatch: false };
        };
        $scope.editLabelInvalid = function () {
            var e = $scope.editing;
            return !!e && !!e.label && !!e.label.trim() && !customerPaymentQr.isValidUpiId(e.label);
        };
        $scope.editLabelMismatch = function () {
            var e = $scope.editing;
            return !!e && mismatch(e.label, e.qr.upiId);
        };
        $scope.canSaveEdit = function () {
            return !!$scope.editing && !$scope.busy && !$scope.editLabelInvalid() &&
                (!$scope.editLabelMismatch() || $scope.editing.confirmMismatch);
        };
        $scope.saveEdit = function () {
            if (!$scope.canSaveEdit()) {
                return;
            }
            var e = $scope.editing;
            run(customerPaymentQr.updateLabel(customerId, e.qr.id, (e.label || '').trim(), e.confirmMismatch), 'Label saved')
                .then(function (ok) {
                    if (ok) {
                        $scope.editing = null;
                    }
                });
        };

        // ---------- order, primary, delete ----------

        $scope.move = function (qr, step) {
            var ids = $scope.qrs.map(function (q) {
                return q.id;
            });
            var from = ids.indexOf(qr.id);
            var to = from + step;
            if ($scope.busy || from < 0 || to < 0 || to >= ids.length) {
                return;
            }
            ids.splice(from, 1);
            ids.splice(to, 0, qr.id);
            run(customerPaymentQr.reorder(customerId, ids), 'Order saved');
        };

        $scope.makePrimary = function (qr) {
            if (!$scope.busy && !qr.primary) {
                run(customerPaymentQr.setPrimary(customerId, qr.id), 'Primary QR set');
            }
        };

        $scope.askDelete = function (qr) {
            if ($scope.busy) {
                return;
            }
            $scope.add = null;
            $scope.replace = null;
            $scope.editing = null;
            $scope.confirmDelete = qr;
        };
        $scope.doDelete = function () {
            var qr = $scope.confirmDelete;
            if (!qr || $scope.busy) {
                return;
            }
            run(customerPaymentQr.remove(customerId, qr.id), 'Payment QR deleted').then(function (ok) {
                if (ok) {
                    $scope.confirmDelete = null;
                }
            });
        };

        $scope.close = function () {
            $uibModalInstance.close();
        };

        $scope.$on('$destroy', function () {
            angular.forEach(urls, function (url) {
                URL.revokeObjectURL(url);
            });
            $scope.cancelAdd();
            $scope.cancelReplace();
        });

        reload();
    });
